import { Route, createRoutesFromElements, useRouteError } from 'react-router-dom';
import { Suspense, lazy } from 'react';
import RouteMap from 'es/app/views/system/RouteMap';
import { Layout } from 'es/app/components/layout';
import ApplicationToaster from 'es/app/components/layout/ApplicationToaster';
import Protected from 'es/app/security/protected';
import IconLibrary from 'es/app/visuals/IconLibrary';
import Throbber from 'es/app/components/progress-indicators/Throbber';
import UnexpectedError from 'es/app/components/error/UnexpectedError';
import { createSiteRouteMap } from 'es/app/views/site/RouteMap';

const People = lazy(() => import('es/app/views/system/people'));
const Facilities = lazy(() => import('es/app/views/system/facilities'));
const AccessGroups = lazy(() => import('es/app/views/system/access-groups'));
const Alerts = lazy(() => import('es/app/views/site/alerts'));
const DataImporters = lazy(() => import('es/app/views/site/data-importers'));
const Processes = lazy(() => import('es/app/views/site/processes'));
const Reports = lazy(() => import('es/app/views/site/reports'));
const Sequences = lazy(() => import('es/app/views/site/sequences'));
const Model = lazy(() => import('es/app/views/site/models'));
const Files = lazy(() => import('es/app/views/site/files'));
const EmissionSources = lazy(() => import('es/app/views/site/emission-sources'));
const EditPeople = lazy(() => import('es/app/views/system/people/edit'));
const EditFacilities = lazy(() => import('es/app/views/system/facilities/edit'));
const EditAccessGroups = lazy(() => import('es/app/views/system/access-groups/edit'));
const EditProcesses = lazy(() => import('es/app/views/site/processes/edit'));
const EditAlert = lazy(() => import('es/app/views/site/alerts/edit'));
const EditReport = lazy(() => import('es/app/views/site/reports/edit'));
const EditSequence = lazy(() => import('es/app/views/site/sequences/edit'));
const EditSequenceForm = lazy(() => import('es/app/views/site/sequences/edit/EditSequence'));
const SequenceExecutionLogs = lazy(() => import('es/app/views/site/sequences/edit/ExecutionLogs'));
const EditDataImporter = lazy(() => import('es/app/views/site/data-importers/edit'));
const EditEmissionSources = lazy(() => import('es/app/views/site/emission-sources/edit'));
const Variables = lazy(() => import('es/app/views/site/processes/edit/variables'));
const EditProcess = lazy(() => import('es/app/views/site/processes/edit/process/EditProcess'));
const EditMonitor = lazy(() => import('es/app/views/site/processes/edit/monitor/EditMonitor'));
const LocationHistory = lazy(() => import('es/app/views/site/processes/edit/location-history/search'));
const EditMonitorLocation = lazy(() => import('es/app/views/site/processes/edit/location-history/edit'));
const Api = lazy(() => import('es/app/views/site/processes/edit/api'));
const ProcessSchemas = lazy(() => import('es/app/views/site/processes/edit/processSchemas'));
const Arena = lazy(() => import('es/app/views/site/processes/edit/arena'));
const LibraryPollutants = lazy(() => import('es/app/views/system/library/pollutants'));
const PollutantDetails = lazy(() => import('es/app/views/system/library/pollutants/PollutantDetails'));
const LibraryUnits = lazy(() => import('es/app/views/system/library/units'));
const UnitDetails = lazy(() => import('es/app/views/system/library/units/UnitDetails'));
const LibraryNoiseClassifiers = lazy(() => import('es/app/views/system/library/noise-classification/index'));
const UpdateNoiseClassifierModel = lazy(
    () => import('es/app/views/system/library/noise-classification/UpdateNoiseClassifierModel')
);
const CreateNoiseClassifierModel = lazy(
    () => import('es/app/views/system/library/noise-classification/CreateNoiseClassifierModel')
);
const AlertConfiguration = lazy(() => import('es/app/views/site/alerts/edit/configuration'));
const AlertRecipients = lazy(() => import('es/app/views/site/alerts/edit/recipients'));
const AlertHistory = lazy(() => import('es/app/views/site/alerts/edit/history'));
const AlertNoise = lazy(() => import('es/app/views/site/alerts/edit/noise/Noise'));
const AlertCorrelation = lazy(() => import('es/app/views/site/alerts/edit/correlation'));
const AlertDataViewer = lazy(() => import('es/app/views/site/alerts/edit/data-viewer'));
const DataViewer = lazy(() => import('es/app/views/site/processes/edit/data-viewer'));
const VariableDataViewer = lazy(() => import('es/app/views/site/processes/edit/variables/VariableDataViewer'));
const ProcessVariableDialog = lazy(
    () => import('es/app/views/site/processes/edit/variables/edit/ProcessVariableDialog')
);
const ProcessDeletion = lazy(() => import('es/app/views/site/processes/edit/deletion'));
const NoiseClassifierProcessSchemaHistory = lazy(
    () =>
        import(
            'es/app/views/site/processes/edit/processSchemas/edit/noiseClassifier/NoiseClassifierProcessSchemaHistory'
        )
);
const EditNoiseClassifierProcessSchemaDefinition = lazy(
    () =>
        import(
            'es/app/views/site/processes/edit/processSchemas/edit/noiseClassifier/EditNoiseClassifierProcessSchemaDefinition'
        )
);
const EditCalpuff = lazy(() => import('es/app/views/site/models/edit/calpuff'));
const EditCalpuffConfigurationForm = lazy(
    () => import('es/app/views/site/models/edit/calpuff/EditCalpuffConfigurationForm')
);
const EditCalmet = lazy(() => import('es/app/views/site/models/edit/calmet'));
const EditCalmetConfigurationForm = lazy(
    () => import('es/app/views/site/models/edit/calmet/EditCalmetConfigurationForm')
);
const EditTrajectory = lazy(() => import('es/app/views/site/models/edit/trajectory'));
const EditTrajectoryConfigurationForm = lazy(
    () => import('es/app/views/site/models/edit/trajectory/EditTrajectoryConfigurationForm')
);
const RunModel = lazy(() => import('es/app/views/site/models/run-model/RunModel'));
const EditFacilityForm = lazy(() => import('es/app/views/system/facilities/edit/details'));
const FacilityUsers = lazy(() => import('es/app/views/system/facilities/edit/users/FacilityUsers'));
const EditFacilityFeatureFlags = lazy(() => import('es/app/views/system/facilities/edit/feature-flags'));
const ReportAlerts = lazy(() => import('es/app/views/site/reports/edit/alerts'));
const ReportDefinition = lazy(() => import('es/app/views/site/reports/edit/definition'));
const ReportHistory = lazy(() => import('es/app/views/site/reports/edit/history'));
const EditPeopleForm = lazy(() => import('es/app/views/system/people/edit/details'));
const EditPeopleAccessGroups = lazy(() => import('es/app/views/system/people/edit/access-groups'));
const EditPeoplePermissionOverrides = lazy(
    () => import('es/app/views/system/people/edit/permission-overrides/PermissionOverrides')
);
const ReportSchedules = lazy(() => import('es/app/views/site/reports/edit/schedules'));
const ReportSettings = lazy(() => import('es/app/views/site/reports/edit/settings'));
const ReportVariables = lazy(() => import('es/app/views/site/reports/edit/variables'));
const EditReportSchedule = lazy(() => import('es/app/views/site/reports/edit/schedules/edit/EditReportSchedule'));
const EditReportScheduleDefinition = lazy(
    () => import('es/app/views/site/reports/edit/schedules/edit/EditReportScheduleDefinition')
);
const ReportScheduleExecutionLogGrid = lazy(
    () => import('es/app/views/site/reports/edit/schedules/edit/ReportScheduleExecutionLogGrid')
);
const TemporalThresholds = lazy(() => import('es/app/views/site/temporal-thresholds/TemporalThresholds'));
const TemporalThresholdDetails = lazy(
    () => import('es/app/views/site/temporal-thresholds/form/details/TemporalThresholdDetails')
);
const TemporalThresholdsForm = lazy(() => import('es/app/views/site/temporal-thresholds/form/TemporalThresholdForm'));
const RecurringThresholds = lazy(
    () => import('es/app/views/site/temporal-thresholds/form/recurring-thresholds/RecurringThresholds')
);
const SpecialThresholds = lazy(
    () => import('es/app/views/site/temporal-thresholds/form/special-thresholds/SpecialThresholds')
);
const ExecutionLogsGrid = lazy(
    () => import('es/app/views/site/data-importers/edit/execution-logs/execution-logs-grid/ExecutionLogsGrid')
);
const DataImporterConfigurationForm = lazy(
    () => import('es/app/views/site/data-importers/edit/configuration/DataImportersConfigurationForm')
);
const EditEmissionSourceForm = lazy(() => import('es/app/views/site/emission-sources/edit/EditEmissionSourceForm'));
const ModelConfigurationStoreProvider = lazy(() => import('es/app/views/site/sequences/edit/ModelConfigurationStore'));

const siteMap = createSiteRouteMap();

export const ROUTES = createRoutesFromElements(
    <>
        <Route
            path="/"
            element={
                <Suspense fallback={<Throbber text="..." />}>
                    <Protected>
                        <IconLibrary />
                        <Layout />
                        <ApplicationToaster />
                    </Protected>
                </Suspense>
            }
            ErrorBoundary={RouteErrorBoundary}
        >
            <Route path={RouteMap.people.search()} element={<People />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={RouteMap.people.create()} element={<EditPeople />}>
                    <Route index element={<EditPeopleForm />} />
                </Route>
                <Route path={RouteMap.people.edit().root} element={<EditPeople />}>
                    <Route index element={<EditPeopleForm />} />
                    <Route path={RouteMap.people.edit().access} element={<EditPeopleAccessGroups />} />
                    <Route path={RouteMap.people.edit().overrides} element={<EditPeoplePermissionOverrides />} />
                </Route>
            </Route>
            <Route path={RouteMap.facilities.search()} element={<Facilities />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={RouteMap.facilities.create()} element={<EditFacilities />}>
                    <Route index element={<EditFacilityForm />} />
                </Route>
                <Route path={RouteMap.facilities.edit().root} element={<EditFacilities />}>
                    <Route index element={<EditFacilityForm />} />
                    <Route path={RouteMap.facilities.edit().users} element={<FacilityUsers />} />
                    <Route path={RouteMap.facilities.edit().features} element={<EditFacilityFeatureFlags />} />
                </Route>
            </Route>
            <Route path={RouteMap.accessGroups.search()} element={<AccessGroups />} ErrorBoundary={RouteErrorBoundary}>
                {[RouteMap.accessGroups.create(), RouteMap.accessGroups.edit()].map(route => (
                    <Route path={route} key={route} element={<EditAccessGroups />} />
                ))}
            </Route>

            <Route
                path={RouteMap.library.search()}
                element={<LibraryPollutants />}
                ErrorBoundary={RouteErrorBoundary}
            />
            <Route
                path={RouteMap.library.pollutants.search()}
                element={<LibraryPollutants />}
                ErrorBoundary={RouteErrorBoundary}
            >
                <Route path={RouteMap.library.pollutants.view().root} element={<PollutantDetails />} />
            </Route>
            <Route path={RouteMap.library.units.search()} element={<LibraryUnits />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={RouteMap.library.units.view().root} element={<UnitDetails />} />
            </Route>
            <Route
                path={RouteMap.library.noiseClassifications.search()}
                element={<LibraryNoiseClassifiers />}
                ErrorBoundary={RouteErrorBoundary}
            >
                <Route path={RouteMap.library.noiseClassifications.create()} element={<CreateNoiseClassifierModel />} />
                <Route
                    path={RouteMap.library.noiseClassifications.view().root}
                    element={<UpdateNoiseClassifierModel />}
                />
            </Route>

            <Route path={siteMap.processes.root} element={<Processes />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={siteMap.processes.root} element={<EditProcesses />}>
                    <Route path={siteMap.processes.variables().root} element={<Variables />}>
                        {[siteMap.processes.variables().create(), siteMap.processes.variables().edit().root].map(
                            path => (
                                <Route key={path} path={path} element={<ProcessVariableDialog />} />
                            )
                        )}
                        <Route
                            key={siteMap.processes.variables().edit().dataViewer}
                            path={siteMap.processes.variables().edit().dataViewer}
                            element={<VariableDataViewer />}
                        />
                    </Route>
                    {[siteMap.processes.create(), siteMap.processes.edit(), siteMap.processes.clone()].map(path => (
                        <Route
                            key={path}
                            path={path}
                            element={
                                <>
                                    <EditMonitor />
                                    <EditProcess />
                                </>
                            }
                        />
                    ))}

                    <Route path={siteMap.processes.locationHistory().root} element={<LocationHistory />} />
                    {[
                        siteMap.processes.locationHistory().edit().root,
                        siteMap.processes.locationHistory().create().root,
                    ].map(path => (
                        <Route key={path} path={path} element={<EditMonitorLocation />} />
                    ))}

                    {[
                        siteMap.processes.api().root,
                        siteMap.processes.api().search(),
                        siteMap.processes.api().create(),
                        siteMap.processes.api().edit(),
                        siteMap.processes.api().transfer(),
                    ].map(path => (
                        <Route path={path} key={path} element={<Api />} />
                    ))}

                    {[siteMap.processes.processSchemas().root, siteMap.processes.processSchemas().search()].map(
                        path => (
                            <Route key={path} path={path} element={<ProcessSchemas />} />
                        )
                    )}
                    {[siteMap.processes.processSchemas().edit().root].map(path => (
                        <Route key={path} path={path} element={<ProcessSchemas />}>
                            <Route
                                path={siteMap.processes.processSchemas().edit().root}
                                index
                                element={<EditNoiseClassifierProcessSchemaDefinition />}
                            />
                            <Route
                                path={siteMap.processes.processSchemas().edit().noiseClassificationHistory}
                                element={<NoiseClassifierProcessSchemaHistory />}
                            />
                        </Route>
                    ))}
                    {[siteMap.processes.processSchemas().create()].map(path => (
                        <Route key={path} path={path} element={<ProcessSchemas />}>
                            <Route
                                index
                                path={siteMap.processes.processSchemas().create()}
                                element={<EditNoiseClassifierProcessSchemaDefinition />}
                            />
                        </Route>
                    ))}

                    <Route path={siteMap.processes.arena().root} element={<Arena />} />
                    <Route path={siteMap.processes.dataViewer().root} element={<DataViewer />} />
                    <Route path={siteMap.processes.deletion().root} element={<ProcessDeletion />} />
                </Route>
            </Route>
            <Route path={siteMap.alerts.search()} element={<Alerts />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={siteMap.alerts.create()} element={<AlertConfiguration />} />
                <Route path={siteMap.alerts.edit().root} element={<EditAlert />}>
                    <Route index element={<AlertConfiguration />} />
                    <Route path={siteMap.alerts.edit().recipients} element={<AlertRecipients />} />
                    <Route path={siteMap.alerts.edit().history} element={<AlertHistory />} />
                    <Route path={siteMap.alerts.edit().noise} element={<AlertNoise />} />
                    <Route path={siteMap.alerts.edit().correlation} element={<AlertCorrelation />} />
                    <Route path={siteMap.alerts.edit().dataViewer} element={<AlertDataViewer />} />
                </Route>
            </Route>
            <Route path={siteMap.reports.search()} element={<Reports />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={siteMap.reports.create()} element={<EditReport />}>
                    <Route index element={<ReportDefinition />} />
                </Route>
                <Route path={siteMap.reports.edit().root} element={<EditReport />}>
                    <Route index element={<ReportDefinition />} />
                    <Route path={siteMap.reports.edit().variables} element={<ReportVariables />} />
                    <Route path={siteMap.reports.edit().alerts} element={<ReportAlerts />} />
                    <Route path={siteMap.reports.edit().schedules.root} element={<ReportSchedules />} />
                    <Route path={siteMap.reports.edit().schedules.create} element={<EditReportSchedule />}>
                        <Route index element={<EditReportScheduleDefinition />} />
                    </Route>
                    <Route path={siteMap.reports.edit().schedules.edit().root} element={<EditReportSchedule />}>
                        <Route index element={<EditReportScheduleDefinition />} />
                        <Route
                            path={siteMap.reports.edit().schedules.edit().executionLog}
                            element={<ReportScheduleExecutionLogGrid />}
                        />
                    </Route>
                    <Route path={siteMap.reports.edit().customisation} element={<ReportSettings />} />
                    {[siteMap.reports.edit().history.root, siteMap.reports.edit().history.deep].map(route => (
                        <Route path={route} element={<ReportHistory />} key={route} />
                    ))}
                </Route>
            </Route>
            <Route path={siteMap.sequences.search()} element={<Sequences />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={siteMap.sequences.edit().root} element={<EditSequence />}>
                    <Route
                        index
                        element={
                            <ModelConfigurationStoreProvider>
                                <EditSequenceForm />
                            </ModelConfigurationStoreProvider>
                        }
                    />
                </Route>
                <Route path={siteMap.sequences.create()} element={<EditSequence />}>
                    <Route
                        index
                        element={
                            <ModelConfigurationStoreProvider>
                                <EditSequenceForm />
                            </ModelConfigurationStoreProvider>
                        }
                    />
                </Route>
                <Route path={siteMap.sequences.edit().executionLogs} element={<EditSequence />}>
                    <Route index element={<SequenceExecutionLogs />} />
                </Route>
            </Route>
            <Route path={siteMap.dataImporters.search()} element={<DataImporters />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={siteMap.dataImporters.create()} element={<EditDataImporter />}>
                    <Route index element={<DataImporterConfigurationForm />} />
                </Route>
                <Route path={siteMap.dataImporters.edit().root} element={<EditDataImporter />}>
                    <Route index element={<DataImporterConfigurationForm />} />
                    <Route
                        path={siteMap.dataImporters.edit().logs}
                        element={<ExecutionLogsGrid excludeColumns={['dataImporter.name']} />}
                    />
                </Route>
            </Route>

            <Route path={siteMap.models.search()} element={<Model />} ErrorBoundary={RouteErrorBoundary}>
                <Route path={siteMap.models.calpuff().create()} element={<EditCalpuff />}>
                    <Route index element={<EditCalpuffConfigurationForm />} />
                </Route>
                <Route path={siteMap.models.calpuff().edit().root} element={<EditCalpuff />}>
                    <Route index element={<EditCalpuffConfigurationForm />} />
                    <Route path={siteMap.models.calpuff().edit().run} element={<RunModel />} />
                </Route>
                <Route path={siteMap.models.calmet().create()} element={<EditCalmet />}>
                    <Route index element={<EditCalmetConfigurationForm />} />
                </Route>
                <Route path={siteMap.models.calmet().edit().root} element={<EditCalmet />}>
                    <Route index element={<EditCalmetConfigurationForm />} />
                    <Route path={siteMap.models.calmet().edit().run} element={<RunModel />} />
                </Route>
                <Route path={siteMap.models.trajectory().create()} element={<EditTrajectory />}>
                    <Route index element={<EditTrajectoryConfigurationForm />} />
                </Route>
                <Route path={siteMap.models.trajectory().edit().root} element={<EditTrajectory />}>
                    <Route index element={<EditTrajectoryConfigurationForm />} />
                    <Route path={siteMap.models.trajectory().edit().run} element={<RunModel />} />
                </Route>
            </Route>
            {[siteMap.files.search(), siteMap.files.searchDeep()].map(route => (
                <Route path={route} key={route} element={<Files />} ErrorBoundary={RouteErrorBoundary} />
            ))}
            <Route
                path={siteMap.emissionSources.search()}
                element={<EmissionSources />}
                ErrorBoundary={RouteErrorBoundary}
            >
                {[siteMap.emissionSources.create(), siteMap.emissionSources.edit().root].map(route => (
                    <Route path={route} key={route} element={<EditEmissionSources />}>
                        <Route index element={<EditEmissionSourceForm />} />
                    </Route>
                ))}
            </Route>
            <Route
                path={siteMap.temporalThresholds.search()}
                element={<TemporalThresholds />}
                ErrorBoundary={RouteErrorBoundary}
            >
                <Route path={siteMap.temporalThresholds.create().root} element={<TemporalThresholdsForm />}>
                    <Route index element={<TemporalThresholdDetails />} />
                    <Route
                        path={siteMap.temporalThresholds.create().recurringThresholds}
                        element={<RecurringThresholds />}
                    />
                    <Route
                        path={siteMap.temporalThresholds.create().specialThresholds}
                        element={<SpecialThresholds />}
                    />
                </Route>
                <Route path={siteMap.temporalThresholds.edit().root} element={<TemporalThresholdsForm />}>
                    <Route index element={<TemporalThresholdDetails />} />
                    <Route
                        path={siteMap.temporalThresholds.edit().recurringThresholds}
                        element={<RecurringThresholds />}
                    />
                    <Route path={siteMap.temporalThresholds.edit().specialThresholds} element={<SpecialThresholds />} />
                </Route>
            </Route>
        </Route>
    </>
);

function UnknownError() {
    return <UnexpectedError error={new Error('Unknown error')} />;
}

function RouteErrorBoundary() {
    const error = useRouteError();
    if (!(error instanceof Error)) return <UnknownError />;
    if (!error || typeof error !== 'object' || !('stack' in error) || typeof error.stack !== 'string')
        return <UnknownError />;
    return <UnexpectedError error={error} />;
}
